// src/pages/ProductAvatarVideos.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateProductAvatarVideoModal from "../components/GenerateProductAvatarVideoModal";
import Loader from "../components/Loader";
import {
  Plus,
  ImagePlus,
  Download,
  Trash2,
  ExternalLink,
  X,
} from "lucide-react";

const FEATURE = "product-avatar-video";
const PAGE_SIZE = 12;

function toCloudinaryAttachmentUrl(url) {
  if (!url || typeof url !== "string") return url;
  return url.includes("/upload/") && !url.includes("/upload/fl_attachment/")
    ? url.replace("/upload/", "/upload/fl_attachment/")
    : url;
}

function isDeletedAsset(a) {
  const s = String(a?.metadata?.status || "").toLowerCase();
  return s === "deleted" || a?.metadata?.deleted === true;
}

export default function ProductAvatarVideos() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const projectId = selectedProduct?.id || paramProjectId;

  const [project, setProject] = useState(null);
  const assets = useMemo(
    () => (Array.isArray(project?.assets) ? project.assets : []),
    [project]
  );

  const avatarVideos = useMemo(
    () =>
      assets
        .filter(
          (a) =>
            (a?.metadata?.feature || "").toLowerCase() === FEATURE &&
            !isDeletedAsset(a)
        )
        .sort(
          (l, r) =>
            new Date(r.updated_at || r.created_at) -
            new Date(l.updated_at || l.created_at)
        ),
    [assets]
  );

  // Legacy (fallback during migration)
  const legacyAvatarImages = useMemo(
    () =>
      Array.isArray(project?.product_avatar_images)
        ? project.product_avatar_images
        : [],
    [project]
  );
  const legacyAvatarVideos = useMemo(
    () =>
      Array.isArray(project?.product_avatar_videos)
        ? project.product_avatar_videos
        : [],
    [project]
  );

  // Pagination + loading states (ObjectRemover-style infinite scroll)
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, assetId?, taskId? }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queueNotice, setQueueNotice] = useState(false);

  // Quota (videos)
  const [quota, setQuota] = useState({
    loading: true,
    blocked: false,
    block_reason: null,
    sub_status: "",
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    videosExtra: 0,
    period_start: null,
    period_end: null,
  });

  const loaderRef = useRef(null);
  const previewBackdrop = useRef(null);
  const pollTimerRef = useRef(null);

  /* --------------------------- Data fetchers --------------------------- */
  const fetchProject = useCallback(
    async (silent = false) => {
      // ✅ If no analyzed project yet, don’t error — show CTA and empty grid.
      if (!projectId) {
        setProject(null);
        setError(null);
        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) setInitialLoading(true);
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProject(data);
        setError(null);
      } catch {
        setError("Failed to fetch project data.");
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [getToken, projectId]
  );

  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-video/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  useEffect(() => {
    setInitialLoading(true);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    fetchProject(false);
    fetchQuota();
  }, [fetchProject, fetchQuota, projectId]);

  // Keep pageRef in sync (so merges can compute hasMore without causing refetch loops)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change
  useEffect(() => {
    const totalCount =
      avatarVideos.length > 0 ? avatarVideos.length : legacyAvatarVideos.length;
    setHasMore(page * PAGE_SIZE < totalCount);
  }, [page, avatarVideos.length, legacyAvatarVideos.length]);

  /* --------------------------- Pending detection + polling --------------------------- */
  const hasPending = useMemo(() => {
    if (avatarVideos.length > 0) {
      return avatarVideos.some(
        (a) => String(a?.metadata?.status || "").toLowerCase() !== "finished"
      );
    }

    return legacyAvatarVideos.some((v) => {
      const status = String(v?.status || "").toLowerCase();
      return status !== "done" && status !== "finished";
    });
  }, [avatarVideos, legacyAvatarVideos]);

  useEffect(() => {
    setQueueNotice(hasPending);
  }, [hasPending]);

  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (hasPending && projectId) {
      pollTimerRef.current = setInterval(() => {
        fetchProject(true);
      }, 3000);
    } else {
      void fetchQuota();
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [hasPending, fetchProject, fetchQuota, projectId]);

  /* --------------------------- Infinite scroll (ObjectRemover pattern) --------------------------- */
  const pagedVideos = useMemo(
    () => avatarVideos.slice(0, page * PAGE_SIZE),
    [avatarVideos, page]
  );

  const pagedLegacyVideos = useMemo(
    () => legacyAvatarVideos.slice(0, page * PAGE_SIZE),
    [legacyAvatarVideos, page]
  );

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  /* --------------------------- Optimistic pending on generate --------------------------- */
  const addOptimisticPending = useCallback(() => {
    const tmpId = `tmp-${Date.now()}`;
    setProject((prev) => {
      const prevAssets = Array.isArray(prev?.assets) ? prev.assets : [];
      const nextAssets = [
        {
          id: tmpId,
          url: "/spinner.gif#pending",
          name: `avatar-video-${tmpId}.mp4`,
          type: "video",
          metadata: { feature: FEATURE, status: "pending", local: true },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        ...prevAssets,
      ];
      return prev ? { ...prev, assets: nextAssets } : { assets: nextAssets };
    });
    setHasMore(true);
  }, []);

  /* --------------------------- Actions --------------------------- */
  const handleDownload = (url) => {
    const finalUrl = toCloudinaryAttachmentUrl(url);
    const a = document.createElement("a");
    a.href = finalUrl;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async ({ assetId, taskId }) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      const token = await getToken();

      if (assetId) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-video/${assetId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setProject((prev) =>
          !prev
            ? prev
            : {
                ...prev,
                assets: (prev.assets || []).filter((a) => a.id !== assetId),
              }
        );

        if (preview?.assetId === assetId) setPreview(null);
        void fetchQuota();
        return;
      }

      // Legacy fallback
      if (taskId) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-video/delete`,
          {
            data: { projectId, taskId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setProject((prev) =>
          !prev
            ? prev
            : {
                ...prev,
                product_avatar_videos: (prev.product_avatar_videos || []).filter(
                  (v) => v.taskId !== taskId
                ),
              }
        );
        if (preview?.taskId === taskId) setPreview(null);
        void fetchQuota();
      }
    } catch {
      // eslint-disable-next-line no-alert
      alert("Delete failed");
    }
  };

  const handlePreview = useCallback(
    async (item) => {
      if (!item) return;
      setPreviewLoading(true);
      try {
        if (item.id && item.url) {
          setPreview({ url: item.url, assetId: item.id });
          return;
        }
        if (item.taskId) {
          const token = await getToken();
          await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-video/status`,
            {
              params: { projectId },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProject(data);
          const latestLegacy = (data.product_avatar_videos || []).find(
            (v) => v.taskId === item.taskId
          );
          setPreview({
            url: latestLegacy?.url || item.url || "",
            taskId: item.taskId,
          });
        }
      } catch {
        setPreview({ url: item.url || "", taskId: item.taskId });
      } finally {
        setPreviewLoading(false);
      }
    },
    [getToken, projectId]
  );

  /* --------------------------- Route sync --------------------------- */
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/product-avatar-video/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  /* --------------------------- Quota-derived flags --------------------------- */
  const blocked = !!quota.blocked;
  const canGenerate = !blocked && (quota.unlimited || quota.remaining > 0);

  const totalVideos = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.videosExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalVideos) &&
    totalVideos > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalVideos) * 100)))
    : 0;

  const isInitialLoading =
    initialLoading &&
    !error &&
    avatarVideos.length === 0 &&
    legacyAvatarVideos.length === 0;

  /* --------------------------- Preview modal close handlers --------------------------- */
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        {/* Title + Quota pill row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Product Avatar Videos
          </h1>

          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                blocked
                  ? "border-red-500/50 bg-red-500/10"
                  : quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                blocked
                  ? "Access blocked (payment required or admin block)"
                  : quota.unlimited
                  ? "Unlimited videos this cycle"
                  : "Videos usage this cycle"
              }
            >
              {blocked ? (
                <span className="text-red-200 font-semibold text-sm sm:text-base">
                  Access blocked — {quota.block_reason || "billing"}
                </span>
              ) : quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited videos
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">
                    {totalVideos === Infinity ? "∞" : totalVideos}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Usage bar */}
        {showBar && (
          <div className="mb-3">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalVideos === Infinity ? "∞" : totalVideos}</span>
            </div>
          </div>
        )}

{queueNotice ? (
          <div className="mb-4 rounded-2xl border border-amber-700/40 bg-amber-600/15 px-4 py-3 text-amber-200">
            <div className="font-semibold text-sm sm:text-base">
              Task queued successfully
            </div>
            <div className="text-xs sm:text-sm text-amber-100/90 mt-1">
              Your request has been queued successfully. It will start shortly, and you’ll receive a notification when it’s ready.
            </div>
          </div>
        ) : null}

        {/* Cross-link to avatar images */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => {
              if (!projectId) {
                navigate("/analyze");
                return;
              }
              navigate(`/generate/product-avatar-image/${projectId}`);
            }}
            className="flex items-center gap-2 rounded-xl px-6 py-2 font-semibold text-white bg-[#23233a] border border-[#353550] shadow hover:text-[#FC2783] hover:shadow-pink-500/40 transition-all duration-200 hover:bg-white hover:border-pink-500 focus:ring-2 focus:ring-pink-600 hover:scale-105"
            style={{
              boxShadow: "0 2px 16px 0 rgba(252,39,131,0.09)",
              minHeight: "44px",
              minWidth: "220px",
            }}
          >
            <ImagePlus size={20} />
            <span className="font-bold">Generate Product Avatar Image</span>
          </button>
        </div>

        {isInitialLoading ? (
          <div className="w-full py-16 flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <>
            {/* Grid */}
            <div
              className="
                grid gap-4 sm:gap-6
                grid-cols-1
                xs:grid-cols-2
                md:grid-cols-3
                lg:grid-cols-4
                [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
              "
            >
              {/* Create Card (always shown; redirects like Remove Background) */}
              <div
                onClick={() => {
                  // ✅ Always show CTA; if no analyzed product, route to Analyze
                  if (!projectId) {
                    navigate("/analyze");
                    return;
                  }
                  // ✅ If out of quota / blocked => Pricing
                  if (!canGenerate) {
                    navigate("/pricing");
                    return;
                  }
                  setModalOpen(true);
                }}
                className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] ${
                  !projectId || !canGenerate
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
                } transition text-center p-4 sm:p-6`}
                title={
                  !projectId
                    ? "Analyze a product first to start generating"
                    : blocked
                    ? "Access blocked. Click to manage billing."
                    : !canGenerate
                    ? "Out of videos. Click to manage plan."
                    : "Pick an input, script and voice to generate"
                }
              >
                <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
                <p className="font-semibold text-base">
                  Create Product Avatar Video
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  {!projectId
                    ? "Analyze your first product to get started"
                    : blocked
                    ? "Payment required or account blocked"
                    : quota.unlimited
                    ? "Unlimited videos"
                    : `You have ${remaining} left of ${
                        totalVideos === Infinity ? "∞" : totalVideos
                      } this cycle`}
                </p>
                {!!projectId && !canGenerate && !blocked && (
                  <p className="text-xs text-red-300 mt-2">
                    You’ve reached your video limit. Upgrade or add videos.
                  </p>
                )}
                {!!projectId && blocked && (
                  <p className="text-xs text-red-200 mt-2">
                    Your subscription needs attention. Click to open pricing.
                  </p>
                )}
              </div>

              {/* Asset cards (assets-first) */}
              {pagedVideos.map((v) => {
                const isPending =
                  (v?.metadata?.status || "").toLowerCase() !== "finished";
                const url = isPending ? "/spinner.gif" : v.url;

                return (
                  <div
                    key={v.id}
                    className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center overflow-hidden"
                  >
                    {isPending ? (
                      <>
                        {/* Delete (aligned like Upscaler: top-right, small button) */}
                        <div className="absolute top-2 right-2 z-20">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete({ assetId: v.id });
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        <img
                          src="/spinner.gif"
                          alt="Generating..."
                          className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <video
                          src={url}
                          className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                          onClick={() => handlePreview(v)}
                          muted
                          loop
                          playsInline
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />

                        {/* Hover overlay — Download, Open, Delete (aligned like Upscaler) */}
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                          style={{ background: "rgba(21, 21, 43, 0.15)" }}
                        >
                          <div className="flex gap-3 pointer-events-auto">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(url);
                              }}
                              className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Open in new tab"
                            >
                              <ExternalLink size={20} />
                            </a>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete({ assetId: v.id });
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Legacy fallback cards (only when no assets yet) */}
              {avatarVideos.length === 0 &&
                pagedLegacyVideos.map((lv) => {
                  const isDone = lv.status === "done";
                  const url = isDone ? lv.url : "/spinner.gif";
                  return (
                    <div
                      key={lv.taskId}
                      className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center overflow-hidden"
                    >
                      {!isDone ? (
                        <>
                          {/* Delete (aligned like Upscaler: top-right, small button) */}
                          <div className="absolute top-2 right-2 z-20">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete({ taskId: lv.taskId });
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>

                          <img
                            src="/spinner.gif"
                            alt="Generating..."
                            className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                            draggable={false}
                            style={{
                              background: "#111124",
                              height: "100%",
                              maxHeight: "calc(100% - 32px)",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <video
                            src={url}
                            className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                            onClick={() => handlePreview(lv)}
                            muted
                            loop
                            playsInline
                            style={{
                              background: "#111124",
                              height: "100%",
                              maxHeight: "calc(100% - 32px)",
                            }}
                          />

                          {/* Hover overlay — Download, Open, Delete (aligned like Upscaler) */}
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                            style={{ background: "rgba(21, 21, 43, 0.15)" }}
                          >
                            <div className="flex gap-3 pointer-events-auto">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(url);
                                }}
                                className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                                title="Download"
                              >
                                <Download size={20} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                                title="Open in new tab"
                              >
                                <ExternalLink size={20} />
                              </a>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete({ taskId: lv.taskId });
                                }}
                                className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                                title="Delete"
                              >
                                <Trash2 size={20} />
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Infinite loader (ObjectRemover-style) */}
            {hasMore && !initialLoading && (
              <div
                ref={loaderRef}
                className="w-full flex justify-center py-8 pointer-events-none"
              >
                {loadingMore && (
                  <img
                    src="/spinner.gif"
                    alt="Loading..."
                    className="w-7 h-7 mx-auto"
                  />
                )}
              </div>
            )}
          </>
        )}

        {/* Modal */}
        {modalOpen && (
          <GenerateProductAvatarVideoModal
            avatarImages={legacyAvatarImages}
            projectId={projectId}
            onClose={() => setModalOpen(false)}
            refreshVideos={() => {
              addOptimisticPending();

              if (!quota.unlimited) {
                setQuota((q) => ({
                  ...q,
                  remaining: Math.max(0, (q.remaining || 0) - 1),
                  used: (q.used || 0) + 1,
                }));
              }

              setQueueNotice(true);
              fetchProject(true);
            }}
          />
        )}

        {/* Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>
              <div className="p-6 flex flex-col items-center justify-center">
                {previewLoading ? (
                  <img
                    src="/spinner.gif"
                    alt="Loading..."
                    className="object-contain w-20 h-20 opacity-80"
                  />
                ) : (
                  <video
                    src={preview.url}
                    controls
                    autoPlay
                    className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                    style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  />
                )}
              </div>
              <div className="flex justify-center gap-4 pb-5">
                <button
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  onClick={() =>
                    handleDelete({
                      assetId: preview.assetId,
                      taskId: preview.taskId,
                    })
                  }
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
              {error && (
                <div className="text-red-500 mt-2 px-6 pb-3">{error}</div>
              )}
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-4">{error}</div>}
      </div>
    </div>
  );
}
